import type { LiveVehicleState } from '@fleetcore/shared/telemetry'
import { DoorOpen, Lightbulb, ShieldCheck } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Badge } from '../../components/ui/badge'
import { Card, CardContent, CardHeader } from '../../components/ui/card'
import { cn } from '../../lib/cn'
import type { CapabilityProfile } from './api-types'
import { CanUnavailableNotice } from './capability-notice'
import {
  decomposeFlagGroup,
  flagLabelKey,
  FLAG_GROUP_KEYS,
  FLAG_GROUP_POLARITY,
  type FlagGroup,
  type FlagGroupKey,
  type FlagIndicator,
} from './state-flags'

/**
 * Ouvrants, ceintures and éclairage as named indicators.
 *
 * Three rules are enforced here and nowhere else:
 *
 *  1. A flag nobody reported renders "Non remonté", never "Fermé". `false` is the
 *     claim that the door is shut; on a vehicle with no CAN adapter that claim is
 *     invented, and it is exactly the plausible-looking wrong answer a fleet manager
 *     cannot detect.
 *  2. A group with nothing reported does not disappear. It renders the CAN sentence
 *     with the program number, because a missing panel and a missing adapter look
 *     identical and only one of them is fixable.
 *  3. Seatbelts are inverted. `true` means FASTENED, so the alarming state is `false`
 *     — a component that treated every raised flag as an alert would paint a green
 *     badge on an unbelted driver.
 */

const GROUP_ICON: Record<FlagGroupKey, LucideIcon> = {
  doors: DoorOpen,
  seatbelts: ShieldCheck,
  lights: Lightbulb,
}

export interface StateIndicatorsProps {
  state: LiveVehicleState | null
  capabilities?: CapabilityProfile
  className?: string
}

export function StateIndicators({ state, capabilities, className }: StateIndicatorsProps) {
  const { t } = useTranslation()
  const groups = FLAG_GROUP_KEYS.map((key) => decomposeFlagGroup(state, key))
  const allAbsent = groups.every((group) => group.absent)

  return (
    <Card className={className}>
      <CardHeader
        title={t('vehicles:flagsPanel.title')}
        description={t('vehicles:flagsPanel.subtitle')}
      />
      <CardContent className="space-y-4">
        {/* One sentence for the whole panel when the bus says nothing at all, rather
            than the same sentence three times. */}
        {allAbsent ? <CanUnavailableNotice profile={capabilities} /> : null}

        <div className="grid gap-4 lg:grid-cols-3">
          {groups.map((group) => (
            <FlagGroupBlock key={group.key} group={group} showAbsenceNotice={!allAbsent} />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function FlagGroupBlock({
  group,
  showAbsenceNotice,
}: {
  group: FlagGroup
  showAbsenceNotice: boolean
}) {
  const { t } = useTranslation()
  const Icon = GROUP_ICON[group.key]

  return (
    <section className="min-w-0 space-y-2">
      <header className="flex items-center justify-between gap-2">
        <h3 className="flex min-w-0 items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-foreground-subtle">
          <Icon aria-hidden className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{t(`vehicles:flagsPanel.group.${group.key}`)}</span>
        </h3>
        <GroupSummary group={group} />
      </header>

      {group.absent ? (
        <p className="text-sm text-foreground-subtle">
          {showAbsenceNotice
            ? t('vehicles:flagsPanel.groupNotReported')
            : t('vehicles:flagsPanel.groupNotReportedShort')}
        </p>
      ) : (
        <ul className="space-y-1">
          {group.indicators.map((indicator) => (
            <IndicatorRow key={indicator.channel} indicator={indicator} groupKey={group.key} />
          ))}
        </ul>
      )}
    </section>
  )
}

function GroupSummary({ group }: { group: FlagGroup }) {
  const { t } = useTranslation()
  if (group.absent) return null

  if (group.alertCount > 0) {
    return (
      <Badge tone="warning">
        {t(`vehicles:flagsPanel.alert.${group.key}`, { count: group.alertCount })}
      </Badge>
    )
  }
  if (group.key === 'lights') {
    return (
      <Badge tone={group.activeCount > 0 ? 'accent' : 'neutral'}>
        {t('vehicles:flagsPanel.alert.lights', { count: group.activeCount })}
      </Badge>
    )
  }
  return <Badge tone="success">{t(`vehicles:flagsPanel.ok.${group.key}`)}</Badge>
}

/**
 * One named flag.
 *
 * The three-way fork is the whole point: set, clear, and never told us. The last one
 * is styled as absent rather than as a negative, so it cannot be skimmed as "closed".
 */
function IndicatorRow({
  indicator,
  groupKey,
}: {
  indicator: FlagIndicator
  groupKey: FlagGroupKey
}) {
  const { t } = useTranslation()
  const polarity = FLAG_GROUP_POLARITY[groupKey]

  const alerting =
    indicator.value === null
      ? false
      : polarity === 'true_is_alert'
        ? indicator.value
        : polarity === 'false_is_alert'
          ? !indicator.value
          : false

  const stateKey =
    indicator.value === null
      ? 'notReported'
      : groupKey === 'doors'
        ? indicator.value
          ? 'open'
          : 'closed'
        : groupKey === 'seatbelts'
          ? indicator.value
            ? 'fastened'
            : 'unfastened'
          : indicator.value
            ? 'on'
            : 'off'

  return (
    <li className="flex items-baseline justify-between gap-2">
      <span className="min-w-0 truncate text-xs text-foreground-muted">
        {t(flagLabelKey(indicator.channel))}
      </span>
      <span
        className={cn(
          'shrink-0 text-xs',
          indicator.value === null
            ? 'italic text-foreground-subtle'
            : alerting
              ? 'font-semibold text-warning'
              : 'text-foreground',
        )}
      >
        {t(`vehicles:flagsPanel.state.${stateKey}`)}
      </span>
    </li>
  )
}
